import { useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { displayName } from '../people';
import { REACTION_SET, groupFor } from '../reactions';

/**
 * THE REACTIONS ON ONE FEED ENTRY — a quiet row of emoji under a comment or
 * update (migration 0143). It shows what has been left (a muted pill per emoji
 * with its count, tinted when it is yours), lets you toggle your own by pressing
 * a pill, and offers the small fixed set behind a "＋" so the row stays short.
 *
 * It reads the store itself — the one cross-board reaction list, the signed-in
 * person and the add/remove toggles — so a feed only has to say WHICH entry this
 * is: the issue it sits on and the entry's id (a comment id or a stands_log id).
 * `canReact` is the same right as commenting; without it the pills still show,
 * read-only, and the add control is hidden.
 */
export default function ReactionBar({
  issueId,
  targetId,
  canReact,
}: {
  issueId: string;
  targetId: string;
  canReact: boolean;
}) {
  const reactions = useApp((s) => s.reactions);
  const profiles = useApp((s) => s.profiles);
  const me = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  const addReaction = useApp((s) => s.addReaction);
  const removeReaction = useApp((s) => s.removeReaction);

  const [picking, setPicking] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // The palette closes on a click anywhere else and on Escape — the same
  // behaviour every menu in the app follows (mousedown, so it beats a click that
  // would otherwise reopen it).
  useEffect(() => {
    if (!picking) return;
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setPicking(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPicking(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [picking]);

  const groups = groupFor(reactions, targetId, me);

  // Pressing an emoji toggles YOUR reaction of it; adding from the palette only
  // ever adds (an emoji already yours is shown as on in the palette and removing
  // is done by pressing its pill).
  const toggle = (emoji: string, mine: boolean) => {
    if (!canReact) return;
    if (mine) removeReaction(issueId, targetId, emoji);
    else addReaction(issueId, targetId, emoji);
  };

  const who = (reactors: string[]) =>
    reactors.map((r) => (r === me ? 'You' : displayName(r, profiles))).join(', ');

  // Nothing to show and nothing to add (a viewer on an entry with no reactions):
  // draw nothing rather than an empty control.
  if (groups.length === 0 && !canReact) return null;

  return (
    <div className="reactbar" ref={wrap}>
      {groups.map((g) => (
        <button
          key={g.emoji}
          type="button"
          className={`react-pill${g.mine ? ' mine' : ''}${canReact ? '' : ' readonly'}`}
          aria-pressed={g.mine}
          disabled={!canReact}
          title={`${who(g.reactors)} reacted ${g.emoji}`}
          onClick={() => toggle(g.emoji, g.mine)}
        >
          <span className="react-emoji" aria-hidden="true">
            {g.emoji}
          </span>
          <span className="react-count">{g.count}</span>
        </button>
      ))}

      {canReact && (
        <div className="react-add">
          <button
            type="button"
            className="react-addbtn"
            aria-label="Add a reaction"
            aria-expanded={picking}
            onClick={() => setPicking((p) => !p)}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20.9 11.3A9 9 0 1 1 12.7 3.1" />
              <path d="M9 9.6h.01M14 9.6h.01" />
              <path d="M8.8 14.2c.8.9 1.9 1.4 3.2 1.4s2.4-.5 3.2-1.4" />
              <path d="M19 2.6v4.6M16.7 4.9h4.6" />
            </svg>
          </button>
          {picking && (
            <div className="react-palette" role="menu" aria-label="Choose a reaction">
              {REACTION_SET.map((emoji) => {
                const mine = groups.some((g) => g.emoji === emoji && g.mine);
                return (
                  <button
                    key={emoji}
                    type="button"
                    role="menuitem"
                    className={`react-choice${mine ? ' mine' : ''}`}
                    aria-pressed={mine}
                    title={mine ? `Take back ${emoji}` : `React ${emoji}`}
                    onClick={() => {
                      toggle(emoji, mine);
                      setPicking(false);
                    }}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
